import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const LoginForm = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = login(username, password);
    
    if (!result.success) {
      setError(result.message);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="login-container">
      <motion.div 
        className="login-form"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="login-header">
          <h2 className="login-title">
            <i className="fas fa-lock me-2"></i>
            Admin Access
          </h2>
          <p className="login-subtitle">Enter your credentials to access the admin dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form-content">
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              <i className="fas fa-user me-2"></i>
              Username
            </label>
            <input
              type="text"
              id="username"
              className="form-control"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              <i className="fas fa-key me-2"></i>
              Password
            </label>
            <input
              type="password"
              id="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          {error && (
            <motion.div 
              className="alert alert-danger"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </motion.div>
          )}

          <motion.button
            type="submit"
            className="btn btn-primary btn-lg w-100"
            disabled={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Signing In...
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt me-2"></i>
                Sign In
              </>
            )}
          </motion.button>
        </form>

        <div className="login-footer">
          <small className="text-muted">
            <i className="fas fa-info-circle me-1"></i>
            Contact the wedding organizers for access credentials
          </small>
        </div>
      </motion.div>

      <style jsx>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #5D7A8C 0%, #8BA6B8 50%, #A8C4D9 100%);
          padding: 2rem;
        }

        .login-form {
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(93, 122, 140, 0.3);
          overflow: hidden;
          max-width: 400px;
          width: 100%;
        }

        .login-header {
          background: linear-gradient(135deg, #5D7A8C, #8BA6B8);
          color: white;
          padding: 2rem;
          text-align: center;
        }

        .login-title {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .login-subtitle {
          margin: 0;
          opacity: 0.9;
          font-size: 0.9rem;
        }

        .login-form-content {
          padding: 2rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #5D7A8C;
        }

        .form-control {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-control:focus {
          outline: none;
          border-color: #5D7A8C;
          box-shadow: 0 0 0 3px rgba(93, 122, 140, 0.1);
        }

        .form-control:disabled {
          background-color: #f8f9fa;
          opacity: 0.6;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background: linear-gradient(135deg, #5D7A8C, #8BA6B8);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #4a6b7a, #7a9ba8);
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(93, 122, 140, 0.3);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-footer {
          padding: 1rem 2rem 2rem;
          text-align: center;
        }

        .alert {
          padding: 0.75rem 1rem;
          border-radius: 10px;
          margin-bottom: 1rem;
        }

        .alert-danger {
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }

        @media (max-width: 480px) {
          .login-container {
            padding: 1rem;
          }
          
          .login-header {
            padding: 1.5rem;
          }
          
          .login-form-content {
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default LoginForm;
